import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../index';
import { Topic } from '../types';

export const useTopics = () => {
  const [data, setData] = useState<Topic[]>([]);
  const [counts, setCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [topicsResult, countsResult] = await Promise.all([
        apiClient.get<Topic[]>('/api/topics'),
        apiClient.get<Record<number, number>>('/api/topics/counts')
      ]);
      setData(topicsResult);
      setCounts(countsResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch topics');
    } finally {
      setLoading(false);
    }
  }, []);

  const createTopic = useCallback(async (topicData: {
    name: string;
    description?: string;
    default_sender_name?: string;
    default_sender_email?: string;
  }) => {
    const result = await apiClient.post<Topic>('/api/topics', topicData);
    await fetchTopics();
    return result;
  }, [fetchTopics]);

  const updateTopic = useCallback(async (id: number, topicData: {
    name?: string;
    description?: string;
    default_sender_name?: string;
    default_sender_email?: string;
  }) => {
    const result = await apiClient.put<Topic>(`/api/topics/${id}`, topicData);
    await fetchTopics();
    return result;
  }, [fetchTopics]);

  const deleteTopic = useCallback(async (id: number) => {
    await apiClient.delete(`/api/topics/${id}`);
    await fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  return {
    data,
    counts,
    loading,
    error,
    refetch: fetchTopics,
    createTopic,
    updateTopic,
    deleteTopic,
  };
};
